/** Shown in place of a value that cannot be calculated yet. */
export const EMPTY_VALUE_PLACEHOLDER = '—';

/**
 * Maps the ngx-translate language codes to BCP 47 tags for `Intl`.
 *
 * @remarks
 * `Intl` is used instead of Angular's `number` pipe because the pipe follows the
 * app-wide `LOCALE_ID` (always `ptBR`), so it would ignore the language the user
 * picked. Unknown languages fall back to `ptBR`, as required by the i18n rules.
 */
const NUMBER_LOCALES: Record<string, string> = {
  ptBR: 'pt-BR',
  en: 'en-US',
  es: 'es-ES',
  it: 'it-IT',
};

const FALLBACK_NUMBER_LOCALE = NUMBER_LOCALES['ptBR'];

const FALLBACK_DECIMAL_SEPARATOR = ',';

// Any number with a fractional part, used to discover the locale's separator
const DECIMAL_SEPARATOR_PROBE = 1.5;

const resolveNumberLocale = (language: string | null | undefined): string =>
  NUMBER_LOCALES[language ?? ''] ?? FALLBACK_NUMBER_LOCALE;

// Formatters are cached because templates format values on every change detection
const formatterCache = new Map<string, Intl.NumberFormat>();

const getFormatter = (locale: string, digits: number): Intl.NumberFormat => {
  const cacheKey = `${locale}:${digits}`;
  let formatter = formatterCache.get(cacheKey);
  if (!formatter) {
    formatter = new Intl.NumberFormat(locale, {
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    });
    formatterCache.set(cacheKey, formatter);
  }
  return formatter;
};

/**
 * Formats a number with a fixed amount of decimals using the separators of the
 * user's language, or returns {@link EMPTY_VALUE_PLACEHOLDER} when there is no value.
 */
export const formatLocalizedNumber = (
  value: number | null | undefined,
  digits: number,
  language: string | null | undefined,
): string => {
  if (value === null || value === undefined) {
    return EMPTY_VALUE_PLACEHOLDER;
  }
  return getFormatter(resolveNumberLocale(language), digits).format(value);
};

/** Decimal separator of the user's language, e.g. `,` for ptBR and `.` for en. */
export const getDecimalSeparator = (
  language: string | null | undefined,
): string => {
  const decimalPart = getFormatter(resolveNumberLocale(language), 1)
    .formatToParts(DECIMAL_SEPARATOR_PROBE)
    .find((part) => part.type === 'decimal');
  return decimalPart?.value ?? FALLBACK_DECIMAL_SEPARATOR;
};
